/**
 * Utility functions for handling URL operations with SSR safety
 */

/**
 * Check if the code is running on the client side (browser)
 * @returns {boolean} True if running in browser, false if server-side
 */
export const isClient = () => {
    return typeof window !== 'undefined';
};

/**
 * Safely execute a function only on the client side
 * @param {Function} callback - Function to execute only on client side
 * @param {*} fallback - Value to return if not on client side (default: undefined)
 * @returns {*} Result of callback or fallback value
 */
export const safeClientExecute = (callback, fallback = undefined) => {
    if (isClient()) {
        return callback();
    }
    return fallback;
};

/**
 * Get current URL object safely
 * @returns {URL|null} URL object or null if not on client side
 */
export const getCurrentURL = () => {
    return safeClientExecute(() => new URL(window.location.href), null);
};

/**
 * Get a query parameter from the current URL
 * @param {string} paramName - Name of the query parameter
 * @returns {string|null} Parameter value or null if not found/not on client side
 */
export const getURLParam = (paramName) => {
    return safeClientExecute(() => {
        const url = getCurrentURL();
        return url !== null ? url.searchParams.get(paramName) : null;
    }, null);
};

/**
 * Set a query parameter in the current URL
 * @param {string} paramName - Name of the query parameter
 * @param {string} value - Value to set
 * @param {boolean} replace - Whether to replace current history entry (default: false)
 * @returns {boolean} True if successful, false if not on client side
 */
export const setURLParam = (paramName, value, replace = false) => {
    return safeClientExecute(() => {
        const url = getCurrentURL();
        if (url === null) {
            return false;
        };

        const currentValue = url.searchParams.get(paramName);

        // Only update if the value is different
        if (currentValue !== value) {
            url.searchParams.set(paramName, value);

            if (replace) {
                history.replaceState({}, '', url);
            } else {
                history.pushState({}, '', url);
            }
        }

        return true;
    }, false);
};

/**
 * Remove a query parameter from the current URL
 * @param {string} paramName - Name of the query parameter to remove
 * @param {boolean} replace - Whether to replace current history entry (default: false)
 * @returns {boolean} True if successful, false if not on client side
 */
export const removeURLParam = (paramName, replace = false) => {
    return safeClientExecute(() => {
        const url = getCurrentURL();
        if (url === null) {
            return false;
        };

        url.searchParams.delete(paramName);

        if (replace) {
            history.replaceState({}, '', url);
        } else {
            history.pushState({}, '', url);
        }

        return true;
    }, false);
};

/**
 * Check if a specific query parameter exists and has a value
 * @param {string} paramName - Name of the query parameter
 * @returns {boolean} True if parameter exists and has a value, false otherwise
 */
export const hasURLParam = (paramName) => {
    const value = getURLParam(paramName);
    return value !== null && value !== undefined && value !== '';
};

/**
 * Parse a page number from URL parameter with fallback
 * @param {string} paramName - Name of the page parameter (default: 'page')
 * @param {number} fallback - Fallback page number (default: 1)
 * @returns {number} Parsed page number or fallback
 */
export const getPageFromURL = (paramName = 'page', fallback = 1) => {
    const pageParam = getURLParam(paramName);
    if (pageParam === null || pageParam === undefined) {
        return fallback;
    }

    const pageNumber = parseInt(pageParam, 10);
    return isNaN(pageNumber) ? fallback : pageNumber;
};
